import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Globe, List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useContent } from "@/contexts/ContentContext";
import BrandLogo from "@/components/BrandLogo";

const FALLBACK_LINKS = [
  { label: "Features", href: "#features", internal: false },
  { label: "About", href: "#about", internal: false },
  { label: "Data", href: "#data", internal: false },
  { label: "Carbon Hub", href: "/carbon-hub", internal: true },
  { label: "Verify", href: "/verify", internal: true },
  { label: "Contact", href: "#contact", internal: false },
];

function NavItem({ link, onClick, isMobile = false }) {
  const className = isMobile
    ? "block text-stone-600 hover:text-stone-900 font-body py-2"
    : "text-sm font-body text-stone-500 hover:text-stone-900 transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-emerald-700 after:transition-all after:duration-300 hover:after:w-full";
  const testId = `nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`;
  if (link.internal || (link.href || "").startsWith("/")) {
    return (
      <Link to={link.href} className={className} onClick={onClick} data-testid={testId}>
        {link.label}
      </Link>
    );
  }
  return (
    <a href={link.href} className={className} onClick={onClick} data-testid={testId}>
      {link.label}
    </a>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { content } = useContent();
  const navbar = content?.navbar || {};
  const logo = navbar.logo_text || "Conexus Foundation";
  const signin = navbar.signin_label || "Sign In";
  const cta = navbar.cta_label || "Live Dashboard";
  const links = Array.isArray(navbar.links) ? navbar.links : FALLBACK_LINKS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-stone-200 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20">
          <a href="#hero" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
            <BrandLogo
              size={36} iconSize={20}
              markClassName="bg-emerald-700 group-hover:scale-110 transition-transform duration-300"
              nameClassName="font-heading text-lg font-semibold text-stone-900 tracking-tight"
              showName={false}
            />
            <span className="font-heading text-lg font-semibold text-stone-900 tracking-tight">{logo}</span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {links.map((link, i) => (
              <NavItem key={`${link.label}-${i}`} link={link} />
            ))}
            <Link to="/login" data-testid="nav-login-link" className="text-sm font-body text-stone-500 hover:text-stone-900 transition-colors">
              {signin}
            </Link>
            <Link to="/dashboard" data-testid="nav-cta-dashboard"
              className="ml-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-6 py-2.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
              {cta}
            </Link>
          </div>

          <button data-testid="mobile-menu-toggle" className="md:hidden p-2 text-stone-700" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-stone-200">
            <div className="px-6 py-4 space-y-3">
              {links.map((link, i) => (
                <NavItem key={`m-${link.label}-${i}`} link={link} isMobile onClick={() => setMobileOpen(false)} />
              ))}
              <Link to="/login" className="block text-stone-600 hover:text-stone-900 font-body py-2" onClick={() => setMobileOpen(false)}>{signin}</Link>
              <Link to="/dashboard" className="block bg-emerald-700 text-white text-center py-2.5 rounded-full font-medium"
                data-testid="mobile-nav-cta-dashboard" onClick={() => setMobileOpen(false)}>{cta}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
